import { useState, useMemo, useEffect } from 'react'
import { Link } from '@tanstack/react-router'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Badge } from './ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { mdiClose, mdiContentSave, mdiPencil, mdiPlus, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from './ui/icon'
import type { components } from '../api/schema'
import { useDocumentTypeProperties, useAddDocumentTypeProperty, useRemoveDocumentTypeProperty, usePropertyDefinitions } from '../hooks/useQueries'
import { useToast } from '../hooks/use-toast'
import { useTranslation } from 'react-i18next'
import { Checkbox } from './ui/checkbox'
import { typeHasPII, withTypePIIFlag } from '../lib/pii'
import { useCan } from '../contexts/PermissionsContext'
import { EmptyState } from './ui/empty-state'

type DocumentType = components['schemas']['DocumentType']
type PropertyDefinition = components['schemas']['PropertyDefinition']

interface DocumentTypeEditorProps {
  org: string
  documentType: DocumentType | null
  allDocumentTypes: DocumentType[]
  onUpdate?: (data: { name?: Record<string, string>; description?: Record<string, string>; parent_document_type_uid?: string | null; flags?: string[] }) => Promise<void>
  onDelete?: () => Promise<void>
}

const DATA_TYPE_LABELS: Record<string, string> = {
  text: 'Text',
  number: 'Number',
  boolean: 'Boolean',
  date: 'Date',
  enum: 'Enum',
  json: 'JSON',
  }

export function DocumentTypeEditor({ org, documentType, allDocumentTypes, onUpdate, onDelete }: DocumentTypeEditorProps) {
  const { toast } = useToast()
  const { t } = useTranslation('common')
  const { t: tTypes } = useTranslation('types')
  // Configuration writes are gated: a read-only caller keeps the whole
  // definition on screen but gets no Edit / Save / Delete and no
  // property-association controls (spec 2026-09-04-02 §2).
  const canWrite = useCan('configuration.write')
  const [nameEn, setNameEn] = useState('')
  const [nameFr, setNameFr] = useState('')
  const [descriptionEn, setDescriptionEn] = useState('')
  const [descriptionFr, setDescriptionFr] = useState('')
  const [parentUid, setParentUid] = useState<string>('')
  // The bare `pii` policy token (spec 2026-08-13-02 §8). Kept as a boolean in
  // form state and merged back into the full flags array on save — `flags`
  // REPLACES the column server-side, so writing ['pii'] would silently destroy
  // any other token the type carries (e.g. `milestone`).
  const [piiFlag, setPiiFlag] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [selectedProperty, setSelectedProperty] = useState<string>('')

  // Load current values when document type changes
  useEffect(() => {
    if (documentType) {
      const name = documentType.name
      if (typeof name === 'object' && name !== null) {
        setNameEn(name.en || '')
        setNameFr(name.fr || '')
      } else {
        setNameEn(name || '')
        setNameFr('')
      }
      const description = documentType.description
      if (typeof description === 'object' && description !== null) {
        setDescriptionEn(description.en || '')
        setDescriptionFr(description.fr || '')
      } else {
        setDescriptionEn('')
        setDescriptionFr('')
      }
      setParentUid(documentType.parent_document_type_uid || '')
      setPiiFlag(typeHasPII((documentType as { flags?: string[] }).flags))
      setIsEditing(false) // Reset editing state when document type changes
    }
  }, [documentType])

  // Queries
  const { data: properties = [], isLoading: isLoadingProperties } = useDocumentTypeProperties(
    org,
    documentType?.uid || ''
  )
  const { data: allProperties = [] } = usePropertyDefinitions(org, { entityType: 'document' })

  // Mutations
  const addPropertyMutation = useAddDocumentTypeProperty(org, documentType?.uid || '')
  const removePropertyMutation = useRemoveDocumentTypeProperty(org, documentType?.uid || '')

  // Helper to get display name
  const getDisplayName = (item: DocumentType | PropertyDefinition) => {
    if (!item?.name) return item?.code || 'Unnamed'
    if (typeof item.name === 'object' && item.name !== null) {
      return item.name.en || item.name.fr || Object.values(item.name)[0] || item.code || 'Unnamed'
    }
    return item.name || item.code || 'Unnamed'
  }

  // Helper to get description
  const getDescription = (property: PropertyDefinition) => {
    if (!property?.description) return ''
    if (typeof property.description === 'object' && property.description !== null) {
      const desc = property.description as Record<string, string>
      return desc.en || desc.fr || Object.values(desc)[0] || ''
    }
    return property.description || ''
  }

  // Never let a read-only caller sit in edit mode, even if the state slipped
  // through (a permission that changed while the page was open).
  const editing = isEditing && canWrite

  // Get available properties (not already associated)
  const availableProperties = useMemo(() => {
    const associatedUids = new Set(properties.map(p => p.uid))
    return allProperties.filter(p => !associatedUids.has(p.uid))
  }, [allProperties, properties])

  const handleSave = async () => {
    if (!canWrite || !onUpdate) return

    try {
      await onUpdate({
        name: { en: nameEn, fr: nameFr },
        description: { en: descriptionEn, fr: descriptionFr },
        parent_document_type_uid: parentUid || null,
        flags: withTypePIIFlag((documentType as { flags?: string[] } | null)?.flags, piiFlag)
      })
      setIsEditing(false)
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update document type',
        variant: 'destructive'
      })
    }
  }

  const handleAddProperty = async () => {
    if (!canWrite || !selectedProperty) return

    try {
      await addPropertyMutation.mutateAsync(selectedProperty)
      setSelectedProperty('')
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to associate property',
        variant: 'destructive'
      })
    }
  }

  const handleRemoveProperty = async (propertyUid: string) => {
    if (!canWrite) return

    try {
      await removePropertyMutation.mutateAsync(propertyUid)
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to remove property',
        variant: 'destructive'
      })
    }
  }

  const handleDelete = async () => {
    if (!canWrite || !onDelete) return

    try {
      await onDelete()
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete document type',
        variant: 'destructive'
      })
    }
  }

  if (!documentType) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center text-muted-foreground">
          <p className="text-lg font-medium mb-2">No document type selected</p>
          <p className="text-sm">Select a document type from the tree to view and edit its details</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">{getDisplayName(documentType)}</h2>
            <p className="text-sm text-muted-foreground mt-1">Code: {documentType.code}</p>
          </div>
          <div className="flex items-center gap-2">
            {canWrite && (editing ? (
              <>
                <Button onClick={() => setIsEditing(false)} variant="outline" size="sm">
                  Cancel
                </Button>
                {onUpdate && (
                  <Button onClick={handleSave} size="sm" data-testid="save-button">
                    <AppIcon path={mdiContentSave} size="sm" className="mr-2" />
                    Save
                  </Button>
                )}
              </>
            ) : (
              <>
                {onUpdate && (
                  <Button onClick={() => setIsEditing(true)} variant="outline" size="sm" data-testid="edit-button">
                    Edit
                  </Button>
                )}
                {onDelete && (
                  <Button onClick={handleDelete} variant="destructive" size="sm" data-testid="delete-button">
                    <AppIcon path={mdiTrashCanOutline} size="sm" className="mr-2" />
                    Delete
                  </Button>
                )}
              </>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Basic Info Section */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name-en">🇺🇸 Name (English)</Label>
                <Input
                  id="name-en"
                  value={nameEn}
                  onChange={(e) => setNameEn(e.target.value)}
                  disabled={!editing}
                  data-testid="name-en-input"
                />
              </div>
              <div>
                <Label htmlFor="name-fr">🇫🇷 Name (French)</Label>
                <Input
                  id="name-fr"
                  value={nameFr}
                  onChange={(e) => setNameFr(e.target.value)}
                  disabled={!editing}
                  data-testid="name-fr-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="description-en">🇺🇸 Description (English)</Label>
                <Input
                  id="description-en"
                  value={descriptionEn}
                  onChange={(e) => setDescriptionEn(e.target.value)}
                  disabled={!editing}
                  placeholder="What this document is and what distinguishes it"
                  data-testid="description-en-input"
                />
              </div>
              <div>
                <Label htmlFor="description-fr">🇫🇷 Description (French)</Label>
                <Input
                  id="description-fr"
                  value={descriptionFr}
                  onChange={(e) => setDescriptionFr(e.target.value)}
                  disabled={!editing}
                  placeholder="Ce qu'est ce document et ce qui le distingue"
                  data-testid="description-fr-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="parent">Parent Document Type</Label>
              <Select
                value={parentUid || 'none'}
                onValueChange={(value) => setParentUid(value === 'none' ? '' : value)}
                disabled={!editing}
              >
                <SelectTrigger data-testid="parent-select">
                  <SelectValue placeholder="No parent (root level)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No parent (root level)</SelectItem>
                  {allDocumentTypes
                    .filter(dt => dt.uid !== documentType.uid)
                    .map(dt => (
                      <SelectItem key={dt.uid} value={dt.uid}>
                        {getDisplayName(dt)}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>

            {/* PII policy (spec 2026-08-13-02 §8). Documents of a flagged type
                inherit `pii:presumed`; a human verdict on a document always
                wins over it. */}
            <div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="document-type-pii"
                  checked={piiFlag}
                  disabled={!editing}
                  onCheckedChange={(checked) => setPiiFlag(checked === true)}
                  data-testid="document-type-pii-checkbox"
                />
                <Label htmlFor="document-type-pii" className="cursor-pointer">
                  {t('pii.type_toggle', { ns: 'common' })}
                </Label>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {t('pii.type_toggle_hint', { ns: 'common' })}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Properties Section */}
        <Card>
          <CardHeader>
            <CardTitle>Associated Properties</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Add Property — the association is a configuration write. */}
            {canWrite && (
            <div className="flex gap-2">
              <Select
                value={selectedProperty}
                onValueChange={setSelectedProperty}
                disabled={availableProperties.length === 0}
              >
                <SelectTrigger className="flex-1" data-testid="property-select">
                  <SelectValue placeholder={
                    availableProperties.length === 0
                      ? 'No properties available'
                      : 'Select a property to add'
                  } />
                </SelectTrigger>
                <SelectContent>
                  {availableProperties.map(prop => (
                    <SelectItem key={prop.uid} value={prop.uid}>
                      <div className="flex items-center gap-2">
                        <span>{getDisplayName(prop)}</span>
                        <Badge variant="outline" className="text-xs">
                          {DATA_TYPE_LABELS[prop.data_type] || prop.data_type}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={handleAddProperty}
                disabled={!selectedProperty || addPropertyMutation.isPending}
                size="sm"
                data-testid="add-property-button"
              >
                <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                Add
              </Button>
            </div>
            )}

            {/* Properties List */}
            <div className="space-y-2">
              {isLoadingProperties ? (
                <div className="text-center py-4 text-sm text-muted-foreground">
                  Loading properties...
                </div>
              ) : properties.length === 0 ? (
                <EmptyState variant="inline" title={tTypes('document_types.no_properties_associated')} />
              ) : (
                properties.map(property => (
                  <div
                    key={property.uid}
                    className={`flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 ${!property.enabled ? 'opacity-50' : ''}`}
                    data-testid={`property-${property.uid}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${!property.enabled ? 'text-muted-foreground' : ''}`}>
                          {getDisplayName(property)}
                          {!property.enabled && <span className="ml-2 text-muted-foreground">(disabled)</span>}
                        </span>
                        <Badge variant="secondary" className="text-xs">
                          {DATA_TYPE_LABELS[property.data_type] || property.data_type}
                        </Badge>
                      </div>
                      {getDescription(property) && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {getDescription(property)}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        Code: {property.code}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Link to={`/${org}/properties/${property.uid}/edit` as any}>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`edit-property-${property.uid}`}
                        >
                          <AppIcon path={mdiPencil} size="sm" />
                        </Button>
                      </Link>
                      {canWrite && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveProperty(property.uid)}
                          disabled={removePropertyMutation.isPending}
                          className="text-destructive hover:text-destructive"
                          data-testid={`remove-property-${property.uid}`}
                        >
                          <AppIcon path={mdiClose} size="sm" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
